import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import { compose } from 'recompose';
import { useHistory, useLocation } from 'react-router-dom';

import { ReactComponent as CreateIcon } from '../../../../assets/icons/v2/creation-plus-circle.svg';
import { PrimaryButton } from '../../../../components/Button/ActionButtons';
import LoadingPage from '../../../../components/LoadingPage';
import MealPlanContext, { withMealPlanContextReady } from '../../../context/MealPlanContext';
import { HeaderContainer, Title } from '../styles';

import MealPlansTable from './components/MealPlansTable';
import ManageMealTemplate from './components/ManageMealTemplate';
import {
  MealAssignmentsTableContainer,
  MealAssignmentsContainer,
} from './styles';
import texts from './texts.json';

const ManageMealPlans = () => {
  const [selectedMealPlan, setSelectedMealPlan] = useState(null);

  const [showCreatePage, setShowCreatePage] = useState(false);
  const { automationTemplate } = useContext(MealPlanContext);
  const { search } = useLocation();
  const history = useHistory();

  const isEditAutomationTemplate = useMemo(() => !!new URLSearchParams(search).get('editAutomationTemplate'), [search]);

  /**
   * Set the automation template as the selected meal plan if we are editing it
   */
  useEffect(() => {
    if (isEditAutomationTemplate && automationTemplate) {
      setShowCreatePage(false);
      setSelectedMealPlan(automationTemplate);
    }
  }, [
    automationTemplate,
    isEditAutomationTemplate,
  ]);

  const handleCreateClick = () => {
    setShowCreatePage((prevShowCreatePage) => !prevShowCreatePage);
  };

  const handleClose = useCallback(() => {
    setShowCreatePage(false);
    setSelectedMealPlan(null);
    // Navigate back to the meal plan settings page
    if (isEditAutomationTemplate) {
      if (history.length > 0) {
        history.goBack();
      }
    }
  }, [isEditAutomationTemplate, history]);

  if (showCreatePage || selectedMealPlan) {
    return (
      <ManageMealTemplate
        selectedMealPlan={selectedMealPlan}
        onClose={handleClose}
        isAutomationTemplate={isEditAutomationTemplate}
      />
    );
  }

  /**
   * Prevent flickering when editing the automation template.
   * Without this, the meal plans table is shown briefly before the transition is complete.
   */
  if (isEditAutomationTemplate) {
    return <LoadingPage />;
  }

  return (
    <MealAssignmentsContainer>
      <HeaderContainer>
        <Title>{texts.manageMealPlansTitle}</Title>
        <PrimaryButton
          onClick={handleCreateClick}
          icon={<CreateIcon />}
        >
          {texts.createMealPlan}
        </PrimaryButton>
      </HeaderContainer>
      <MealAssignmentsTableContainer>
        <MealPlansTable onSelectMealPlan={setSelectedMealPlan} />
      </MealAssignmentsTableContainer>
    </MealAssignmentsContainer>
  );
};

export default compose(
  withMealPlanContextReady,
)(ManageMealPlans);
